import React from 'react';
import {
  CogIcon,
  DocumentTextIcon,
  SparklesIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  Cog6ToothIcon,
  TableCellsIcon,
  ArrowRightIcon,
  LightBulbIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  UserGroupIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

export default function ProcessCreatorHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full mb-6">
            <CogIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Process Creator Guide</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Design, document, and automate your business processes with intelligent workflow management
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-teal-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is Process Creator?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your intelligent process design studio. Create standardized workflows, break them down into actionable steps, and leverage AI to generate comprehensive training materials. It's like having a process architect and training department rolled into one powerful tool that ensures consistency and quality across your operations.
              </p>
            </div>
          </div>
        </div>

        {/* Process Creation Workflow Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">The Process Creation Workflow</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Design Process</h3>
              <p className="text-gray-600 text-sm">Create a new process with name, description, and assign relevant roles</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-teal-400" />
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Build Steps</h3>
              <p className="text-gray-600 text-sm">Add tasks and substeps with detailed instructions and role assignments</p>
            </div>
          </div>

          {/* Mobile flow indicator */}
          <div className="md:hidden flex justify-center items-center space-x-4 mt-6">
            <span className="text-teal-600 font-medium">1 → 2</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate Training</h3>
              <p className="text-gray-600 text-sm">Use AI to automatically create training documents and materials</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-teal-400" />
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Deploy & Train</h3>
              <p className="text-gray-600 text-sm">Publish processes and provide interactive training to your team</p>
            </div>
          </div>

          {/* Mobile flow indicator */}
          <div className="md:hidden flex justify-center items-center space-x-4 mt-6">
            <span className="text-teal-600 font-medium">3 → 4</span>
          </div>
        </div>

        {/* Key Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Feature 1 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <PlusIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Process Creation</h3>
            <p className="text-gray-600 text-sm">
              Start fresh processes with custom names, descriptions, and role assignments that fit your business needs
            </p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <TableCellsIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Step Management</h3>
            <p className="text-gray-600 text-sm">
              Build detailed workflows with tasks, substeps, and role-specific instructions for each process stage
            </p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <CpuChipIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Generation</h3>
            <p className="text-gray-600 text-sm">
              Leverage artificial intelligence to automatically generate comprehensive training materials and documentation
            </p>
          </div>

          {/* Feature 4 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <UserGroupIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Role Assignment</h3>
            <p className="text-gray-600 text-sm">
              Assign specific roles to each step and task, ensuring the right people handle the right responsibilities
            </p>
          </div>

          {/* Feature 5 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <DocumentTextIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Training Integration</h3>
            <p className="text-gray-600 text-sm">
              Seamlessly convert processes into interactive training courses with quizzes and progress tracking
            </p>
          </div>

          {/* Feature 6 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <PencilIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Process Editing</h3>
            <p className="text-gray-600 text-sm">
              Modify existing processes, update steps, and refine workflows as your business processes evolve
            </p>
          </div>
        </div>

        {/* Understanding Process Components */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Understanding Process Components</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Process Structure */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <CogIcon className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Process Structure</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                A process is a complete workflow with a name, description, and assigned roles. Each process contains multiple steps that guide users through the workflow.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-800 text-sm"><strong>Hierarchy:</strong> Process → Steps → Substeps. Each level provides more detailed instructions.</p>
              </div>
            </div>

            {/* Step Types */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <TableCellsIcon className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Step Components</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Steps are the main building blocks of your process. Each step can have substeps for more granular instructions.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Tasks: Main actions to be performed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Substeps: Detailed breakdown of tasks</span>
                </div>
              </div>
            </div>

            {/* AI Generation */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <CpuChipIcon className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">AI-Powered Training</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Automatically generate training materials from your processes. The AI creates engaging content, quizzes, and interactive learning experiences.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <p className="text-purple-800 text-sm">AI analyzes your process structure and creates comprehensive training documents with visual elements.</p>
              </div>
            </div>

            {/* Training Delivery */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Interactive Training</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Deliver training through engaging courses with progress tracking, quizzes, and certification to ensure staff competency.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">Track completion rates and quiz performance to measure training effectiveness.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Project Types & Materials Management */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Project Types & Materials Management</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Project Types */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">External vs Internal Projects</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                When creating a new project, choose between External (client-facing) or Internal (company operations) project types.
              </p>
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-blue-800 text-sm font-semibold mb-1">External Projects</p>
                  <p className="text-blue-700 text-xs">Include pricing, materials tracking, and client invoicing capabilities. Ideal for customer-facing work.</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <p className="text-gray-800 text-sm font-semibold mb-1">Internal Projects</p>
                  <p className="text-gray-700 text-xs">Focus on operations, processes, and team coordination without client billing features.</p>
                </div>
              </div>
            </div>

            {/* Materials Tab */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Materials Tab (External Only)</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                External projects unlock the Materials tab, allowing you to track inventory, costs, and usage for accurate billing and resource management.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Add materials with quantities and unit costs</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Track bought vs used status per material</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Visual dashboard shows usage statistics</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Auto-include used materials in invoices</span>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Project Pricing</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Set project prices during creation to establish budget expectations and enable accurate invoicing for External projects.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">The price field helps track profitability by comparing quoted price against material costs and labor time.</p>
              </div>
            </div>

            {/* Materials Workflow */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <TableCellsIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Materials Workflow</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                From the Materials tab, add items with name, category, quantity, unit cost, supplier, and notes. Mark items as bought when purchased, then used when consumed in tasks.
              </p>
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                <p className="text-indigo-800 text-sm">Only materials marked "used" appear on invoices, ensuring clients are billed only for actual consumption.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Substep Types Guide */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Substep Types & Their Purpose</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Cog6ToothIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Action Steps</h3>
              <p className="text-sm text-gray-600">Specific actions or tasks that need to be performed. These are the "how-to" instructions.</p>
            </div>

            <div className="text-center p-6 bg-amber-50 rounded-lg border border-amber-200">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircleIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Bridge Steps</h3>
              <p className="text-sm text-gray-600">Transitional elements that connect different parts of the process or explain context.</p>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-lg border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <EyeIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Expectation Steps</h3>
              <p className="text-sm text-gray-600">Quality standards, outcomes, or expectations that should be met during the process.</p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-6 md:p-8 border border-teal-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-teal-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Process Creation Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Start with clear, descriptive process names that immediately convey purpose</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Break complex processes into logical, manageable steps</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Assign specific roles to each step to ensure accountability</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use substeps for detailed instructions while keeping main steps high-level</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Include quality expectations and success criteria in your processes</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Test processes with actual users before finalizing and generating training</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use AI generation to create consistent training materials across all processes</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Regularly review and update processes to reflect current best practices</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Include decision points and conditional logic where processes can branch</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Document exceptions and edge cases to prepare staff for unusual situations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}